import {ChangeDetectionStrategy, Component, computed, inject, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AppContext} from "../../../app-context";
import {
  BezoekschipOrganisation,
  CargoTransactionSummary,
  DateTimeRange,
  FacetValueResult,
  GetVisit,
  IE3BulkAddAuthorisationInConsignments,
  IE3BulkChangeTerminalOfConsignments,
  IE3ConsignmentProcessSummary,
  IE3ConsignmentSortOrder,
  IE3ConsignmentSortType,
  IE3FilingType,
  IE3FindConsignmentProcesses,
  IE3FindConsignments,
  IE3GetTotalWeightOfConsignments,
  IE3RegisterNotificationOfArrival,
  Terminal,
  Visit,
  VisitSummary
} from "@portbase/bezoekschip-service-typescriptmodels";
import {combineLatest, from, Observable, of} from "rxjs";
import {openConfirmationModalWithCallback, openEditModal, sendCommand, sendQuery} from "../../../common/utils";
import {WebsocketService} from "../../../common/websocket.service";
import {SentenceCasePipe} from "../../../common/sentence-case.pipe";
import {ConsignmentUtils} from "../consignment.utils";
import {ActivatedRoute, Router} from "@angular/router";
import lodash, {isNaN, parseInt} from "lodash";
import {
  MasterConsignmentDetailsComponent,
  MasterConsignmentDetailsComponentData
} from "../details/master-consignment-details/master-consignment-details.component";
import {DataQuery, FacetedOverview} from "../../common/overview/faceted-overview";
import {FacetedOverviewFilters} from "../../common/overview/overview.component";
import {OverviewFilters, QuickView} from "../../common/facets/facet-filter/facet-filter.component";
import {PortvisitUtils} from "../../../refdata/portvisit-utils";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../../common/modal/modal-confirm.component";
import {ConsignmentDownloadUtils} from "../consignment-download.utils";
import {ConsignmentRules} from "../consignment.rules";
import {Handler} from "../../../common/handlers/handler";
import {HandleQuery} from "../../../common/handlers/handle-query";
import {NavigationItem} from "../../common/overview-navigation-tabs/overview-navigation-tabs.component";
import {DateFieldRange} from "../../../common/date/date-range/date-field-range";
import moment from "moment";
import {NdjsonService} from "../../../common/ndjson-status/ndjson.service";

const quickViews: QuickView[] = [{
  code: "ALL_DECLARATIONS",
  name: "All declarations",
  icon: "fa-file-lines",
  hidden: () => AppContext.isAdmin() && AppContext.isProduction(),
  facets: {}
}, {
  code: "INBOUND",
  name: "Inbound",
  icon: "fa fa-xl fa-arrow-down-to-line",
  facets: {
  },
  facetsFn: () => {return AppContext.useOpenSearch() ? {containsTranshipment: ["false"]} : {isTranshipment: ["false"]}}
}, {
  code: "TRANSHIPMENT",
  name: "Transhipment",
  icon: "fa-kit fa-xl fa-pb-ship-to-ship",
  facets: {
  },
  facetsFn: () => {return AppContext.useOpenSearch() ? {containsTranshipment: ["true"]} : {isTranshipment: ["true"]}}
}, {
  code: "ACTION_REQUIRED",
  name: "Attention required",
  icon: "fa-circle-exclamation",
  facets: {},
  queryFn: (query: DataQuery<IE3FindConsignments>): DataQuery<IE3FindConsignments> => {
    return ({
      ...query,
      payload: {
        ...query.payload,
        attentionRequired: true
      }
    });
  }
}];

@Component({
  selector: 'app-consignments-overview',
  templateUrl: './consignments-overview.component.html',
  styleUrls: ['./consignments-overview.component.scss'],
  providers: [WebsocketService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
@Handler()
export class ConsignmentsOverviewComponent extends FacetedOverview<IE3ConsignmentProcessSummary, ConsignmentOverviewFilters> implements OnInit {
  private downloadService = inject(NdjsonService);
  protected router = inject(Router);

  protected readonly utils = ConsignmentUtils;
  appContext = AppContext;
  data: IE3ConsignmentProcessSummary[] = [];
  facetOrdering: string[] = ["masterConsignment/customsProcess", "masterConsignment/customsStatus",
    "placeOfLoading", "placeOfUnloading", "dischargeTerminalName", "declarantName",
    "temporaryStorageStatus", "ensStatus", "transhipment/renTaskStatus", "transhipmentMidTaskStatus", "transhipment/midStatus/status",
    "arrivalStatus", "filingType", "transhipment/declarantFullName"];

  timeOfArrival: string;
  @ViewChild("registerArrivalModalBody") registerArrivalModalBody: TemplateRef<any>;
  @ViewChild("bulkUpdateTerminalModalBody") bulkUpdateTerminalModalBody: TemplateRef<any>;
  @ViewChild("bulkAddAuthorisationModalBody") bulkAddAuthorisationModalBody: TemplateRef<any>;
  @ViewChild("downloadModalBody") downloadModalBody: TemplateRef<any>;
  @ViewChild("downloadMrnModalBody") downloadMrnModalBody: TemplateRef<any>;
  @ViewChild("downloadZhsModal") downloadZhsModal: TemplateRef<any>;

  downloadCrn: string;
  downloadVoyageNumber: string;
  selectedTerminal: Terminal;
  selectableTerminals: Terminal[];
  selectedOrganisation: BezoekschipOrganisation;

  downloadZhsBulkData: boolean = true;
  downloadZhsDateRange: DateFieldRange = {
    start: moment().startOf('year').toISOString(),
    end: moment().add(1, 'year').startOf('year').toISOString()
  };
  bulkDataFormatter: (value) => (any) = v => v ? "Bulk" : "Container";

  visit: Visit;
  selectedDeclarant: BezoekschipOrganisation;
  weightsLoading: boolean;
  private _grossWeight: number;
  protected cargoNavigationTabs: NavigationItem[] = [{
    text: "Consignments",
    routerLink: '/cargo',
    icon: 'fa-file-lines'
  }];
  maxItems = 50;

  isSelectable = (record: IE3ConsignmentProcessSummary) => ConsignmentRules.isSummaryAllowedToDeclare(record);

  allowedToAddAnything = computed(() => ConsignmentUtils.allowedToAddConsignment() || ConsignmentUtils.allowedToAddTranshipment());

  constructor(private websocket: WebsocketService<IE3ConsignmentProcessSummary>, route: ActivatedRoute) {
    super(undefined, quickViews, quickViews.find(q => q.code === "ACTION_REQUIRED"));
    this.maxItems = 50;
    route.params.subscribe(params => {
      route.queryParams.subscribe(queryParams => {
        this.filters.overviewFilters.filters.crn = this.downloadCrn = params["crn"];
        this.onCrnChange(this.filters.overviewFilters.filters.crn);
        const cargoDeclarantId = params["cargoDeclarantId"];
        if (this.filters.overviewFilters.filters.crn) {
          this.filters.overviewFilters.facets = this.parseParams(queryParams);
          this.filters.overviewFilters.selectedQuickView = this.quickViews[0];
          if (cargoDeclarantId) {
            this.filters.overviewFilters.hiddenFacets = this.filters.overviewFilters.hiddenFacets || {};
            this.filters.overviewFilters.hiddenFacets.iamConnectedId = [cargoDeclarantId];
          }
        } else {
          this.setLocalStorageKey("consignment-overview-filters");
        }
        this.cargoNavigationTabs.push({
          text: "Equipment",
          routerLink: `/container${this.filters.overviewFilters.filters.crn ? '/' + this.filters.overviewFilters.filters.crn : ''}`,
          icon: 'fa-container-storage'
        });
        this.updateGrossWeight();
        if (this.loadCount > 0) {
          this.loadData();
        }
      })
    });
  }

  parseParams(params: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};

    for (const key in params) {
      const value = params[key];

      let typeValue;
      // Attempt to convert strings to proper types
      if (value === 'true') typeValue = true;
      else if (value === 'false') typeValue = false;
      else if (!isNaN(Number(value))) typeValue = Number(value);
      else typeValue = value;

      if (!result[key]) {
        result[key] = [typeValue];
      } else {
        result[key].push(typeValue);
      }
    }

    return result;
  }


  protected facetFiltersChanged(overviewFilters: OverviewFilters<any>) {
    super.facetFiltersChanged(overviewFilters);
    this.updateGrossWeight();
  }

  getDataQuery(term: string): DataQuery<IE3FindConsignments> {
    if (!this.filters.overviewFilters.filters.timeRange) {
      this.updateTimeRangeToDefault(term);
    }
    const timeRange = this.prepareDateTimeRange(this.filters.overviewFilters.filters.timeRange,
      !term, this.filters.overviewFilters.filters.timeRange);
    const queryFn = this.filters.overviewFilters.selectedQuickView?.queryFn;
    const query = <DataQuery<IE3FindConsignments>>{
      className: "com.portbase.bezoekschip.common.api.consignments.queries.FindConsignments",
      payload: {
        term: term,
        crn: this.filters.overviewFilters.filters.crn,
        dateTimeRange: term ? null : timeRange,
        pagination: {from: this.from, count: this.maxItems},
        facetFilters: this.getFacetFilters(),
        sortOrder: this.filters.overviewFilters.filters.crn ? IE3ConsignmentSortOrder.ASCENDING : null,
        sortType: this.filters.overviewFilters.filters.crn ?
          IE3ConsignmentSortType.CONSIGNMENT : null
      }
    };
    return queryFn ? queryFn(query) : query;
  }

  ngOnInit(): void {
    this.websocket.initialise(`/api/ui/consignments`,
      (update: IE3ConsignmentProcessSummary) => this.updateFromWebsocket(update), true);
  }

  createConsignment = () => openEditModal(MasterConsignmentDetailsComponent, <MasterConsignmentDetailsComponentData>{
    consignmentProcessId: null,
    crn: this.filters.overviewFilters.filters.crn
  }, {
    warnOnClose: true
  });

  renderRecords = (result: IE3ConsignmentProcessSummary[], append: boolean) => {
    this.saveFiltersInLocalStorage(this.filters);
    if (result.length < this.maxItems) {
      this.endReached = true;
    }
    if (!append) {
      this.clearSelection();
    }
    this.data = append ? [...(this.data || []), ...result] : [...result];
  }

  trackByRecord(index: number, record: IE3ConsignmentProcessSummary): any {
    return record.masterConsignment.consignmentNumber;
  }

  private updateFromWebsocket(update: IE3ConsignmentProcessSummary) {
    const index = this.data.findIndex(c => c.consignmentProcessId === update.consignmentProcessId);
    if (index >= 0) {
      this.data = [...this.data.slice(0, index), update, ...this.data.slice(index + 1)];
    }
  }

  nameFormatter = (name: string): string => {
    switch (name) {
      case "placeOfLoading":
        return "Place of loading";
      case "placeOfUnloading":
        return "Port of discharge";
      case "dischargeTerminalName":
        return "Discharge terminal";
      case "filingType":
        return "Filing type";
      case "houseConsignmentsCount":
        return "Number of house consignments";
      case "equipmentCount":
        return "Number of equipments";
      case "masterConsignment/customsProcess":
        return "Customs process";
      case "masterConsignment/customsStatus":
        return "Customs status";
      case "ensStatus":
        return "Entry summary declaration";
      case "temporaryStorageStatus":
        return "Temporary storage";
      case "declarantName":
        return "Cargo declarant";
      case "hasShortLandedContainers":
        return "Shortlanded equipment";
      case "hasClearanceDifferences":
        return "Clearance differences";
      case "conflictingTerminals":
        return "Discharge terminal mismatch";
      case "transhipment/renTaskStatus":
        return "Re-export notification";
      case "transhipment/midStatus/status":
      case "transhipmentMidTaskStatus":
        return "Notification import documentation";
      case "transhipment/declarantFullName":
        return "Transhipment declarant";
      case "transhipment/scheduled":
        return "Transhipment scheduled";
      default:
        return SentenceCasePipe.formatWithSpaces(name);
    }
  }

  getValueFormatter = (name: string): (value: string) => string => {
    switch (name) {
      case "filingType":
        return (v) => ConsignmentUtils.filingTypeFormatter(v as IE3FilingType, false);
      case "dischargeTerminalName":
        return SentenceCasePipe.format;
      case "masterConsignment/customsProcess":
        return (v) => ConsignmentUtils.getCustomsProcessCode(v as any);
      case "masterConsignment/customsStatus":
        return (v) => ConsignmentUtils.getCustomsStatusCode(v as any);
      case "ensStatus":
        return (v) => PortvisitUtils.enumFormatter(v).replace("Ens", "ENS");
      case "arrivalStatus":
      case "transhipment/midStatus/status":
      case "transhipment/renTaskStatus":
      case "transhipmentMidTaskStatus":
        return PortvisitUtils.enumFormatter;
      case "temporaryStorageStatus":
        return (v) => ConsignmentUtils.temporaryStorageStatusValueFormatter(v);
      default:
        return null;
    }
  }

  getHiddenFacets = (): string[] => {
    return ["count", "equipmentCount", "totalGrossWeight", "houseConsignmentsCount", "containerNumbers", "allowedViewers",
      "iamConnectedId", "activeCrns", "fromVisit", "actionRequired", "attentionRequiredForAgents", "transhipment/midStatus/status"]
      .concat(AppContext.isAdmin() ? [] : ["declarantName", "transhipment/declarantFullName"]);
  }

  get masterConsignmentCount(): number {
    return lodash.sum(this.allFacets?.find(f => f.name === "count")?.values
      .map(v => v.count)) || 0;
  }

  get houseConsignmentCount(): number {
    if (AppContext.useOpenSearch()) {
      return lodash.sum(this.allFacets?.find(f => f.name === "houseConsignmentsCount")?.values
        .map(v => v.count)) || 0;
    }
    return lodash.sum(this.allFacets?.find(f => f.name === "houseConsignmentsCount")?.values
      .map(v => v.count * parseInt(v.value))) || 0;
  }

  get equipmentCount(): number {
    if (AppContext.useOpenSearch()) {
      return lodash.sum(this.allFacets?.find(f => f.name === "equipmentCount")?.values
        .map(v => v.count)) || 0;
    }
    return lodash.sum(this.allFacets?.find(f => f.name === "equipmentCount")?.values
      .map(v => v.count * parseInt(v.value))) || 0;
  }

  get grossWeight(): number {
    if (AppContext.useOpenSearch()) {
      return lodash.sum(this.allFacets?.find(f => f.name === "totalGrossWeight")?.values
        .map(v => v.count)) || 0;
    }
    return this._grossWeight;
  }

  get anyRecordSelectable(): boolean {
    return this.data?.length && this.data.some(d => this.isSelectable(d));
  }

  getSelectedConsignments(): IE3ConsignmentProcessSummary[] {
    return this.data.filter(c => this.isRecordSelected(c));
  }

  get nrOfSelectedConsignments(): number {
    return this.getSelectedConsignments().length;
  }

  openBulkAddAuthorisationModal = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      sendCommand("com.portbase.bezoekschip.common.api.consignments.commands.BulkAddAuthorisationInConsignments", <IE3BulkAddAuthorisationInConsignments>{
        organisationShortName: this.selectedOrganisation.shortName,
        consignments: this.getSelectedConsignments().map(c => c.consignmentProcessId)
      }, () => {
        AppContext.registerSuccess("Successfully added authorisation to the selected consignments")
        this.clearSelection();
      });
    }
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "Add authorisation",
    confirmText: "Declare consignments",
    cancelText: "Cancel",
    modalSize: "lg",
    body: this.bulkAddAuthorisationModalBody
  }, 'static');

  openBulkUpdateTerminalModal = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      sendCommand("com.portbase.bezoekschip.common.api.consignments.commands.BulkChangeTerminalOfConsignments", <IE3BulkChangeTerminalOfConsignments>{
        crn: this.visit.crn,
        terminal: this.selectedTerminal,
        consignments: this.getSelectedConsignments().map(c => c.consignmentProcessId)
      }, () => {
        AppContext.registerSuccess("Successfully updated the terminal of the selected consignments")
        this.clearSelection();
      });
    }
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "Bulk update terminal",
    confirmText: "Declare consignments",
    cancelText: "Cancel",
    modalSize: "lg",
    body: this.bulkUpdateTerminalModalBody
  }, 'static');

  openRegisterArrivalModal = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      sendCommand("com.portbase.bezoekschip.common.api.consignments.commands.RegisterNotificationOfArrival", <IE3RegisterNotificationOfArrival>{
        cargoDeclarantId: this.appContext.userProfile.organisation?.iamConnectedId,
        consignmentProcessIds: this.getSelectedConsignments().map(s => s.consignmentProcessId),
        ata: this.timeOfArrival
      }, () => this.clearSelection());
    }
    this.timeOfArrival = null;
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "Register arrival",
    confirmText: "Register arrival",
    cancelText: "Cancel",
    modalSize: "lg",
    body: this.registerArrivalModalBody
  }, 'static');

  downloadMrns = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      return this.findConsignmentProcesses({
        crn: this.downloadCrn,
        voyageNumber: this.downloadVoyageNumber,
        cargoDeclarantId: this.selectedDeclarant?.iamConnectedId
      }).subscribe(c => ConsignmentDownloadUtils.downloadMrns(this.downloadCrn || this.downloadVoyageNumber, c));
    }
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "Download MRNs",
    confirmText: "Download",
    cancelText: "Close",
    body: this.downloadMrnModalBody
  }, 'static');

  downloadBlDetails = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      return combineLatest([
        this.downloadCrn ? this.getVisit(this.downloadCrn) : of(null),
        this.findConsignmentProcesses({
          crn: this.downloadCrn,
          voyageNumber: this.downloadVoyageNumber,
          cargoDeclarantId: this.selectedDeclarant?.iamConnectedId
        })
      ]).subscribe(c => c[0]
        ? ConsignmentDownloadUtils.downloadBlDetailsUsingVisit(c[0], c[1])
        : ConsignmentDownloadUtils.downloadBlDetailsUsingVoyageNumber(this.downloadVoyageNumber, c[1]));
    }
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "B/L details download",
    confirmText: "Download",
    cancelText: "Close",
    body: this.downloadModalBody
  }, 'static');

  downloadZhsData = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      const timeRange: DateTimeRange = {
        start: moment(this.downloadZhsDateRange.start).toISOString(),
        end: moment(this.downloadZhsDateRange.end).toISOString()
      };
      const summaries: CargoTransactionSummary[] = [];
      const url = `/api/cargoThroughPutData/download?start=${timeRange.start}&end=${timeRange.end}&bulk=${this.downloadZhsBulkData}`;
      from(this.downloadService.startDownload(url, r => summaries.push(r.value)))
        .subscribe(_ => this.downloadZhsBulkData
          ? ConsignmentDownloadUtils.exportCargoThroughputBulkToExcel(timeRange, summaries)
          : ConsignmentDownloadUtils.exportCargoThroughputEquipmentToExcel(timeRange, summaries));
    }
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "Download throughput data",
    confirmText: "Download",
    cancelText: "Close",
    body: this.downloadZhsModal
  }, 'static');

  downloadClearanceDifferences = () => openConfirmationModalWithCallback((confirmed) => {
    if (confirmed) {
      return combineLatest([this.getVisit(this.downloadCrn), this.findConsignmentProcesses({
        crn: this.downloadCrn,
        cargoDeclarantId: this.selectedDeclarant?.iamConnectedId,
        facetFilters: [{
          facetName: "clearanceDifferences",
          values: ["true"]
        }]
      })]).subscribe(c => ConsignmentDownloadUtils.downloadClearanceDifferences(c[0], c[1]));
    }
  }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
    type: "primary",
    title: "Clearance differences",
    confirmText: "Download",
    cancelText: "Close",
    body: this.downloadModalBody
  }, 'static');

  protected onCrnChange = (crn: string) => {
    this.downloadCrn = crn;
    if (crn) {
      this.getVisit(crn).subscribe((v: Visit) => {
        this.visit = v;
        this.selectableTerminals = ConsignmentUtils.getTerminals(v);
      });
    } else {
      this.visit = null;
      this.selectableTerminals = [];
      this._grossWeight = null;
    }
  }

  private updateGrossWeight = () => {
    if (AppContext.useOpenSearch()) {
      return;
    }
    if (!this.filters.overviewFilters.filters.crn) {
      this._grossWeight = null;
      return;
    }
    this.weightsLoading = true;
    sendQuery("com.portbase.bezoekschip.common.api.consignments.queries.GetTotalWeightOfConsignments", <IE3GetTotalWeightOfConsignments>{
      crn: this.filters.overviewFilters.filters.crn,
      facetFilters: this.getFacetFilters()
    }).subscribe(c => {
      this._grossWeight = c;
      this.weightsLoading = false;
    });
  }

  protected crnFilterValueFormatter = (visit: VisitSummary | string) =>
    (visit as VisitSummary)?.crn || (visit as string);

  protected crnFilterChanged = (visit: VisitSummary | string) => {
    this.filters.overviewFilters.filters.crn = this.crnFilterValueFormatter(visit);
    this.router.navigateByUrl(`/cargo${this.filters.overviewFilters.filters.crn ? '/' + this.filters.overviewFilters.filters.crn : ''}`)
  }

  findVisits = (term) => ConsignmentUtils.findVisits(term, {
    start: this.filters.overviewFilters.filters.timeRange?.start,
    end: this.filters.overviewFilters.filters.timeRange?.end
  });

  private getVisit = (crn: string) =>
    sendQuery("com.portbase.bezoekschip.common.api.visit.GetVisit", <GetVisit>{crn: crn});
  private findConsignmentProcesses = (query: IE3FindConsignmentProcesses) =>
    sendQuery("com.portbase.bezoekschip.common.api.consignments.queries.FindConsignmentProcesses", query, {
      showSpinner: true
    });

  @HandleQuery("getFacetValues", {global: true})
  getFacetValues(facetName: string): Observable<FacetValueResult[]> {
    if (Object.keys(this.filters.overviewFilters.facets).length) {
      return super.getFacetValues(facetName);
    }
    return of(this.allFacets.find(f => f.name === facetName).values);
  }
}

interface ConsignmentOverviewFilters extends FacetedOverviewFilters<ConsignmentOverviewFacetFilters> {
}

interface ConsignmentOverviewFacetFilters {
  timeRange?: DateFieldRange;
  crn?: string;
}
